import React, { useRef, useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';

const LiquidGlassBox = ({
  children,
  variant = "primary",
  size = "medium",
  padding = "auto",
  margin = "0",
  width = "auto",
  height = "auto",
  borderRadius = "auto",
  shadow = "auto",
  blur = "auto",
  border = "auto",
  background = "auto",
  hover = true,
  parallax = true,
  className = "",
  style = {},
  onClick,
  ...props
}) => {
  const boxRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  
  // Motion values for parallax effect
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  // Transform mouse position to rotation and translation
  const rotateX = useTransform(mouseY, [-100, 100], [8, -8]);
  const rotateY = useTransform(mouseX, [-100, 100], [-8, 8]);
  const translateZ = useTransform(mouseX, [-100, 100], [-8, 8]);
  
  // Spring animations for smooth transitions
  const springConfig = { damping: 20, stiffness: 200 };
  const springRotateX = useSpring(rotateX, springConfig);
  const springRotateY = useSpring(rotateY, springConfig);
  const springTranslateZ = useSpring(translateZ, springConfig);

  // Handle mouse movement for parallax effect
  useEffect(() => {
    if (!parallax) return;

    const handleMouseMove = (e) => {
      if (!boxRef.current) return;
      
      const rect = boxRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const x = e.clientX - centerX;
      const y = e.clientY - centerY;
      
      mouseX.set(x);
      mouseY.set(y);
      setMousePosition({ x, y });
    };

    const handleMouseLeave = () => {
      mouseX.set(0);
      mouseY.set(0);
      setMousePosition({ x: 0, y: 0 });
    };

    const box = boxRef.current;
    if (box) {
      box.addEventListener('mousemove', handleMouseMove);
      box.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (box) {
        box.removeEventListener('mousemove', handleMouseMove);
        box.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [mouseX, mouseY, parallax]);

  // Variant styles
  const variants = {
    primary: {
      background: `
        linear-gradient(135deg, 
          rgba(255, 255, 255, 0.1) 0%, 
          rgba(255, 255, 255, 0.05) 50%, 
          rgba(255, 255, 255, 0.1) 100%
        ),
        radial-gradient(
          circle at 30% 30%, 
          rgba(255, 255, 255, 0.15) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(255, 255, 255, 0.3)',
      shadowColor: 'rgba(0, 123, 255, 0.2)',
      textColor: 'rgba(255, 255, 255, 0.9)'
    },
    secondary: {
      background: `
        linear-gradient(135deg, 
          rgba(255, 255, 255, 0.08) 0%, 
          rgba(255, 255, 255, 0.03) 50%, 
          rgba(255, 255, 255, 0.08) 100%
        ),
        radial-gradient(
          circle at 70% 70%, 
          rgba(255, 255, 255, 0.12) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(255, 255, 255, 0.2)',
      shadowColor: 'rgba(255, 255, 255, 0.15)',
      textColor: 'rgba(255, 255, 255, 0.8)'
    },
    success: {
      background: `
        linear-gradient(135deg, 
          rgba(40, 167, 69, 0.1) 0%, 
          rgba(40, 167, 69, 0.05) 50%, 
          rgba(40, 167, 69, 0.1) 100%
        ),
        radial-gradient(
          circle at 30% 30%, 
          rgba(40, 167, 69, 0.15) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(40, 167, 69, 0.3)',
      shadowColor: 'rgba(40, 167, 69, 0.2)',
      textColor: 'rgba(255, 255, 255, 0.9)'
    },
    danger: {
      background: `
        linear-gradient(135deg, 
          rgba(220, 53, 69, 0.1) 0%, 
          rgba(220, 53, 69, 0.05) 50%, 
          rgba(220, 53, 69, 0.1) 100%
        ),
        radial-gradient(
          circle at 70% 70%, 
          rgba(220, 53, 69, 0.15) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(220, 53, 69, 0.3)',
      shadowColor: 'rgba(220, 53, 69, 0.2)',
      textColor: 'rgba(255, 255, 255, 0.9)'
    },
    warning: {
      background: `
        linear-gradient(135deg, 
          rgba(255, 193, 7, 0.1) 0%, 
          rgba(255, 193, 7, 0.05) 50%, 
          rgba(255, 193, 7, 0.1) 100%
        ),
        radial-gradient(
          circle at 30% 30%, 
          rgba(255, 193, 7, 0.15) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(255, 193, 7, 0.3)',
      shadowColor: 'rgba(255, 193, 7, 0.2)',
      textColor: 'rgba(255, 255, 255, 0.9)'
    },
    info: {
      background: `
        linear-gradient(135deg, 
          rgba(23, 162, 184, 0.1) 0%, 
          rgba(23, 162, 184, 0.05) 50%, 
          rgba(23, 162, 184, 0.1) 100%
        ),
        radial-gradient(
          circle at 70% 70%, 
          rgba(23, 162, 184, 0.15) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(23, 162, 184, 0.3)',
      shadowColor: 'rgba(23, 162, 184, 0.2)',
      textColor: 'rgba(255, 255, 255, 0.9)'
    },
    dark: {
      background: `
        linear-gradient(135deg, 
          rgba(0, 0, 0, 0.3) 0%, 
          rgba(0, 0, 0, 0.1) 50%, 
          rgba(0, 0, 0, 0.3) 100%
        ),
        radial-gradient(
          circle at 30% 30%, 
          rgba(255, 255, 255, 0.05) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(255, 255, 255, 0.1)',
      shadowColor: 'rgba(0, 0, 0, 0.4)',
      textColor: 'rgba(255, 255, 255, 0.8)'
    }
  };

  // Size styles
  const sizes = {
    small: {
      padding: '16px',
      borderRadius: '8px',
      blur: '15px',
      shadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      border: '1px solid'
    },
    medium: {
      padding: '24px',
      borderRadius: '12px',
      blur: '20px',
      shadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
      border: '1px solid'
    },
    large: {
      padding: '32px',
      borderRadius: '16px',
      blur: '25px',
      shadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
      border: '1px solid'
    },
    xlarge: {
      padding: '48px',
      borderRadius: '24px',
      blur: '30px',
      shadow: '0 32px 80px rgba(0, 0, 0, 0.25)',
      border: '1px solid'
    }
  };

  const currentVariant = variants[variant] || variants.primary;
  const currentSize = sizes[size] || sizes.medium;

  // Apply custom values or use defaults
  const finalPadding = padding === "auto" ? currentSize.padding : padding;
  const finalBorderRadius = borderRadius === "auto" ? currentSize.borderRadius : borderRadius;
  const finalBlur = blur === "auto" ? currentSize.blur : blur;
  const finalShadow = shadow === "auto" ? currentSize.shadow : shadow;
  const finalBorder = border === "auto" ? `${currentSize.border} ${currentVariant.borderColor}` : border;
  const finalBackground = background === "auto" ? currentVariant.background : background;

  return (
    <motion.div
      ref={boxRef}
      className={`liquid-glass-box ${className}`}
      style={{
        position: 'relative',
        width,
        height,
        margin,
        padding: finalPadding,
        borderRadius: finalBorderRadius,
        background: finalBackground,
        backdropFilter: `blur(${finalBlur})`,
        WebkitBackdropFilter: `blur(${finalBlur})`,
        backgroundBlendMode: 'overlay',
        border: finalBorder,
        boxShadow: finalShadow,
        color: currentVariant.textColor,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transformStyle: 'preserve-3d',
        perspective: '1000px',
        overflow: 'hidden',
        ...style
      }}
      whileHover={hover ? {
        scale: 1.02,
        boxShadow: `
          0 20px 60px rgba(0, 0, 0, 0.3),
          0 0 20px ${currentVariant.shadowColor},
          inset 0 1px 0 rgba(255, 255, 255, 0.2)
        `,
        filter: 'brightness(1.05)',
        transition: { duration: 0.3 }
      } : undefined}
      animate={{
        rotateX: parallax && isHovered ? springRotateX : 0,
        rotateY: parallax && isHovered ? springRotateY : 0,
        translateZ: parallax && isHovered ? springTranslateZ : 0,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      {...props}
    >
      {/* Glass overlay effect */}
      <motion.div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(
              135deg,
              rgba(255, 255, 255, 0.1) 0%,
              transparent 50%,
              rgba(255, 255, 255, 0.05) 100%
            )
          `,
          borderRadius: 'inherit',
          pointerEvents: 'none',
          mixBlendMode: 'overlay'
        }}
        animate={{
          opacity: isHovered ? 0.8 : 0.4
        }}
        transition={{ duration: 0.3 }}
      />
      
      {/* Inner glow effect */}
      <motion.div
        style={{
          position: 'absolute',
          top: '2px',
          left: '2px',
          right: '2px',
          bottom: '2px',
          background: `
            radial-gradient(
              circle at 50% 0%,
              rgba(255, 255, 255, 0.1) 0%,
              transparent 70%
            )
          `,
          borderRadius: 'inherit',
          pointerEvents: 'none',
          filter: 'blur(1px)'
        }}
        animate={{
          opacity: isHovered ? 0.4 : 0.2
        }}
        transition={{ duration: 0.3 }}
      />

      {/* Corner highlights */}
      <motion.div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%),
            linear-gradient(-45deg, transparent 30%, rgba(255, 255, 255, 0.05) 50%, transparent 70%)
          `,
          borderRadius: 'inherit',
          pointerEvents: 'none'
        }}
        animate={{
          opacity: isHovered ? 0.6 : 0.3
        }}
        transition={{ duration: 0.3 }}
      />

      {/* Content with proper z-index */}
      <motion.div
        style={{
          position: 'relative',
          zIndex: 1,
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
        animate={{
          y: isHovered ? 2 : 0
        }}
        transition={{ duration: 0.2 }}
      >
        {children}
      </motion.div>

      {/* Hover glow effect */}
      {isHovered && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{
            position: 'absolute',
            top: '-2px',
            left: '-2px',
            right: '-2px',
            bottom: '-2px',
            borderRadius: 'inherit',
            background: `linear-gradient(45deg, ${currentVariant.shadowColor}, transparent)`,
            zIndex: -1,
            filter: 'blur(8px)'
          }}
          transition={{ duration: 0.4 }}
        />
      )}
    </motion.div>
  );
};

export default LiquidGlassBox; 